/**
 * Yallet RWA SDK - Encryption Module
 *
 * Provides ECIES encryption for RWA assets using xidentity public keys.
 * Compatible with the Yallet WASM encryption module.
 */

import type {
  AssetType,
  AssetEnvelope,
  EncryptedPayload,
  EncryptFunction,
} from './types.js';

// ======================================
//  Pure JavaScript ECIES Implementation
// ======================================

/**
 * ECIES encryption using Web Crypto API.
 * Must match dev.yallet.chrome-extension (acegf WASM) so the extension can decrypt:
 *   dh_key = SHA256(shared_secret), encrypted_aes_key = AES-GCM(dh_key).encrypt(iv, aes_key), encrypted_data = AES-GCM(aes_key).encrypt(iv, plaintext).
 */
export class ECIESEncryptor {
  /**
   * Encrypt data for a recipient's xidentity public key.
   * Scheme (same as acegf encrypt): ECDH → SHA256(dh) = master_key → AES-GCM(master_key).encrypt(iv, random_aes_key) → AES-GCM(aes_key).encrypt(iv, plaintext).
   *
   * @param recipientXidentityB64 - Recipient's X25519 public key (base64)
   * @param plaintext - Data to encrypt
   * @returns Encrypted payload
   */
  async encrypt(
    recipientXidentityB64: string,
    plaintext: Uint8Array
  ): Promise<EncryptedPayload> {
    const recipientPubKey = base64ToBytes(recipientXidentityB64);

    const ephemeralKeyPair = (await crypto.subtle.generateKey(
      { name: 'X25519' },
      true,
      ['deriveBits']
    )) as CryptoKeyPair;

    const ephemeralPubKeyRaw = await crypto.subtle.exportKey(
      'raw',
      ephemeralKeyPair.publicKey
    );
    const ephemeralPubKey = new Uint8Array(ephemeralPubKeyRaw);

    const recipientKey = await crypto.subtle.importKey(
      'raw',
      recipientPubKey as BufferSource,
      { name: 'X25519' },
      false,
      []
    );

    const sharedSecretRaw = await crypto.subtle.deriveBits(
      { name: 'X25519', public: recipientKey },
      ephemeralKeyPair.privateKey,
      256
    );
    const sharedSecret = new Uint8Array(sharedSecretRaw);

    // Match extension WASM: dh_key = SHA256(shared_secret), then AES-GCM(dh_key) encrypts the random AES key
    const dhKeyHash = await crypto.subtle.digest('SHA-256', sharedSecret);
    const dhKey = new Uint8Array(dhKeyHash);

    const masterKey = await crypto.subtle.importKey(
      'raw',
      dhKey,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const aesKeyBytes = crypto.getRandomValues(new Uint8Array(32));
    const aesKey = await crypto.subtle.importKey(
      'raw',
      aesKeyBytes,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encryptedAesKeyRaw = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      masterKey,
      aesKeyBytes
    );
    const encryptedAesKey = new Uint8Array(encryptedAesKeyRaw);

    const encryptedDataRaw = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      plaintext as BufferSource
    );
    const encryptedData = new Uint8Array(encryptedDataRaw);

    return {
      ephemeral_pub: bytesToBase64(ephemeralPubKey),
      encrypted_aes_key: bytesToBase64(encryptedAesKey),
      iv: bytesToBase64(iv),
      encrypted_data: bytesToBase64(encryptedData),
    };
  }
}

// ======================================
//  WASM-Compatible Encryption Wrapper
// ======================================

/**
 * Wrapper for WASM encrypt function
 *
 * Use this when you have access to the Yallet WASM module
 */
export function createWasmEncryptor(wasmEncryptFn: EncryptFunction) {
  return {
    encrypt(
      recipientXidentityB64: string,
      plaintext: Uint8Array
    ): EncryptedPayload {
      const result = wasmEncryptFn(recipientXidentityB64, plaintext);

      if (result.startsWith('error:')) {
        throw new Error(`Encryption failed: ${result.substring(6)}`);
      }

      return JSON.parse(result) as EncryptedPayload;
    },
  };
}

/**
 * Initialize acegf WASM and return the encrypt function for createRWASDK.
 * Pass the default export of acegf.js (init) and optionally the wasm path/buffer.
 *
 * @example
 * ```ts
 * import init from './wasm/acegf.js';
 * import { createAcegfEncryptor, createRWASDK } from '@yallet/rwa-sdk';
 * const wasmEncryptFn = await createAcegfEncryptor(init, '/path/to/acegf_bg.wasm');
 * const sdk = createRWASDK(config, { wasmEncryptFn });
 * ```
 */
export async function createAcegfEncryptor(
  initFn: (
    moduleOrPath?: string | URL | ArrayBuffer | object
  ) => Promise<{ acegf_encrypt_for_xidentity: EncryptFunction }>,
  wasmModuleOrPath?: string | URL | ArrayBuffer | object
): Promise<EncryptFunction> {
  const acegf = await initFn(wasmModuleOrPath);
  return acegf.acegf_encrypt_for_xidentity;
}

// ======================================
//  Asset Preparation
// ======================================

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Options for preparing asset data
 */
export interface PrepareAssetOptions {
  /** Custom UUID (auto-generated if not provided) */
  uuid?: string;
  /** Custom timestamp (current time if not provided) */
  timestamp?: number;
  /** Schema identifier */
  schema?: string;
  /** Previous token for version chain */
  previousToken?: string;
}

/**
 * Prepare asset data for encryption
 *
 * @param asset - Asset data object
 * @param assetType - Type of asset
 * @param options - Optional configuration
 * @returns Object containing serialized data and metadata
 */
export function prepareAssetData(
  asset: unknown,
  assetType: AssetType,
  options: PrepareAssetOptions = {}
): { data: Uint8Array; uuid: string; timestamp: number } {
  const uuid = options.uuid || generateUUID();
  const timestamp = options.timestamp || Date.now();

  const envelope: AssetEnvelope = {
    version: 1,
    type: assetType,
    timestamp,
    uuid,
    schema: options.schema,
    previousToken: options.previousToken,
    data: asset,
  };

  const jsonString = JSON.stringify(envelope);
  return {
    data: new TextEncoder().encode(jsonString),
    uuid,
    timestamp,
  };
}

/**
 * Options for encrypting an asset
 */
export interface EncryptAssetOptions {
  /** Custom UUID (auto-generated if not provided) */
  uuid?: string;
  /** Custom timestamp (current time if not provided) */
  timestamp?: number;
  /** Schema identifier */
  schema?: string;
  /** Previous token for version chain */
  previousToken?: string;
}

/**
 * Result of encrypting an asset
 */
export interface EncryptedAssetResult extends EncryptedPayload {
  assetType: AssetType;
  version: number;
  uuid: string;
  timestamp: number;
  dataSize: number;
}

/**
 * Encrypt an asset for a recipient
 *
 * @param asset - Asset data
 * @param assetType - Type of asset
 * @param recipientXidentity - Recipient's xidentity (base64)
 * @param encryptor - Encryptor instance (WASM or JS)
 * @param options - Optional configuration
 * @returns Encrypted payload with metadata
 */
export async function encryptAsset(
  asset: unknown,
  assetType: AssetType,
  recipientXidentity: string,
  encryptor: ECIESEncryptor | ReturnType<typeof createWasmEncryptor>,
  options: EncryptAssetOptions = {}
): Promise<EncryptedAssetResult> {
  const prepared = prepareAssetData(asset, assetType, options);

  let encrypted: EncryptedPayload;

  if (encryptor instanceof ECIESEncryptor) {
    encrypted = await encryptor.encrypt(recipientXidentity, prepared.data);
  } else {
    encrypted = encryptor.encrypt(recipientXidentity, prepared.data);
  }

  return {
    ...encrypted,
    assetType,
    version: 1,
    uuid: prepared.uuid,
    timestamp: prepared.timestamp,
    dataSize: prepared.data.length,
  };
}

// ======================================
//  Utility Functions
// ======================================

function base64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binaryString = '';
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }
  return btoa(binaryString);
}
